import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import login_page from "../../assets/Logofile/Favicon.png";
import white_logo from "../../assets/Logofile/logo (2).png";
import { loginUser } from "../../redux/slices/authSlice";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === "rememberMe" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const resultAction = await dispatch(
        loginUser({
          email: formData.email,
          password: formData.password,
        })
      );

      if (loginUser.fulfilled.match(resultAction)) {
        navigate("/dashboard");
      } else {
        const error = resultAction.payload || "Login failed";
        console.log(error);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        fontFamily: "inherit",
      }}
    >
      {/* Right Side */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
          position: "relative",
        }}
      >
        {/* Floating Circles */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "20%",
              left: "10%",
              width: 40,
              height: 40,
              bgcolor: "rgba(99, 102, 241, 0.1)",
              borderRadius: "50%",
              animation: "float 4s ease-in-out infinite",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "60%",
              left: "80%",
              width: 60,
              height: 60,
              bgcolor: "rgba(236, 72, 153, 0.1)",
              borderRadius: "50%",
              animation: "float 4s 0.5s ease-in-out infinite",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "30%",
              left: "70%",
              width: 30,
              height: 30,
              bgcolor: "rgba(16, 185, 129, 0.1)",
              borderRadius: "50%",
              animation: "float 4s 1s ease-in-out infinite",
            }}
          />
        </Box>

        <Paper
          elevation={3}
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
            width: "100%",
            maxWidth: 450,
            borderRadius: 2,
            p: { xs: 3, md: 5 },
            zIndex: 1,
            position: "relative",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              component="img"
              src={white_logo}
              alt="Logo"
              sx={{
                height: { xs: 40, md: 80 },
                mb: { xs: 2, md: 3 },
                display: "block",
                mx: "auto",
              }}
            />
            <Typography variant="h4" color="text.primary" sx={{ mb: 1 }}>
              Welcome to Jeevandeep Foundation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Access your startup dashboard to discover the next unicorn startup
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              id="email"
              name="email"
              label="Professional Email"
              placeholder="your@business.com"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              id="password"
              name="password"
              label="Secure Password"
              placeholder="••••••••"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="primary" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                fontSize: 16,
                boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
                height: 48,
                mb: 2,
                textTransform: "none",
                background: "linear-gradient(90deg, #6366f1 0%, #a855f7 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #6366f1 20%, #a855f7 100%)",
                  boxShadow: "0 6px 16px rgba(99,102,241,0.4)",
                  transform: "translateY(-2px)",
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
              endIcon={<ArrowForwardIcon />}
            >
              {loading ? "Logging in..." : "Access My Portfolio"}
            </Button>
          </form>
        </Paper>

        {/* Illustration */}
        <Box
          component="img"
          src={login_page}
          alt="Login Illustration"
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: 150,
            maxWidth: "35%",
            height: "auto",
            objectFit: "contain",
            zIndex: 2,
            pointerEvents: "none",
            display: { xs: "none", md: "block" },
          }}
        />
      </Box>

      {/* Keyframes for floating circles */}
      <style>
        {`
        @keyframes float {
          0% { transform: translateY(0px);}
          50% { transform: translateY(-20px);}
          100% { transform: translateY(0px);}
        }
        `}
      </style>
    </Box>
  );
};

export default Login;
